import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  CustomerServiceOutlined,
  AppstoreOutlined,
  StopOutlined
} from '@ant-design/icons'
import { Pto } from '@outcoreaix/pto'
import { Button, Divider, Flex, Typography } from 'antd'
import Icon from '@ant-design/icons'
import { useGetSpacesQuery } from 'api/spaces-api'
import { AppRoutes } from 'app/app-routes'
import { authStorage } from 'app/auth-storage'
import clsx from 'clsx'
import ConditionalWrapper from 'components/ConditionalWrapper'
import Feedback from 'features/users/components/Feedback'
import { FC } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { RootState } from 'app/store'
import { generateSpaceSettingsMenu } from '../menu-items'
import SpacesIcon from 'assets/spaces-icon'

type Props = {
  onMenuItemClick?: () => void
  collapsed?: boolean
  workspace?: Pto.Workspaces.WorkspaceOption
  currentWorkspaceId?: number
  isDesktop?: boolean
}
const UserMenuItem: FC<Props> = ({ collapsed, workspace, currentWorkspaceId, onMenuItemClick, isDesktop }) => {
  const user = authStorage.user
  const params = useParams()
  const navigate = useNavigate()
  const currentSpaceId = useSelector((state: RootState) => state.chat.currentSpaceId)

  const { data: spacesData } = useGetSpacesQuery(
    {
      workspaceId: currentWorkspaceId!
    },
    { skip: currentWorkspaceId === undefined }
  )
  const spaces = spacesData?.items.filter((item) => item.workspaceId === Number(currentWorkspaceId))

  const firstActiveSpace = spaces?.find((space) => space.status === Pto.Spaces.SpaceStatus.Active)
  const activeSpaceId = currentSpaceId || firstActiveSpace?.id

  const onSettingsClick = () => {
    if (!workspace || !workspace.currentMember) return

    if (workspace.currentMember.role === Pto.Workspaces.WorkspaceRole.Contact) {
      navigate(`/settings/${workspace.id}/profile-settings`)
    } else {
      if (activeSpaceId) {
        navigate(`/settings/${workspace.id}/space/${activeSpaceId}/members`)
      } else {
        navigate(`/settings/${workspace.id}/members`)
      }
    }
    onMenuItemClick?.()
  }

  if (
    !isDesktop &&
    workspace?.currentMember?.role !== Pto.Workspaces.WorkspaceRole.Contact &&
    !user?.roles.includes(Pto.Users.UserRole.SystemAdmin)
  ) {
    return (
      <div className="max-xl:pb-4 md:sticky xl:block bottom-0 bg-white z-10">
        <Flex vertical gap={0}>
          <Button
            type="text"
            icon={<UserOutlined className="[&>svg]:w-[18px] [&>svg]:h-[18px]" />}
            className="w-full flex h-10 justify-normal hover:!bg-secondaryWithOpacity font-medium"
            onClick={() => {
              navigate(`${AppRoutes.settings}/${workspace?.id}/profile-settings`)
              onMenuItemClick?.()
            }}
          >
            Profile settings
          </Button>
          <Button
            type="text"
            icon={<CustomerServiceOutlined className="[&>svg]:w-[18px] [&>svg]:h-[18px]" />}
            className="w-full flex h-10 justify-normal hover:!bg-secondaryWithOpacity font-medium"
            onClick={() => {
              navigate(`${AppRoutes.settings}/${workspace?.id}/support`)
              onMenuItemClick?.()
            }}
          >
            Support
          </Button>
          {workspace?.currentMember?.role === Pto.Workspaces.WorkspaceRole.Admin && (
            <Button
              type="text"
              icon={<AppstoreOutlined className="[&>svg]:w-[18px] [&>svg]:h-[18px]" />}
              className="w-full flex h-10 justify-normal hover:!bg-secondaryWithOpacity font-medium"
              onClick={() => {
                navigate(`/settings/${workspace?.id}/general`)
                onMenuItemClick?.()
              }}
            >
              College Settings
            </Button>
          )}
          {spaces && spaces.length > 0 && (
            <>
              <Divider className="mx-0 my-2" />
              <div className="flex gap-2 items-center px-3.5 py-2 text-gray-500">
                <Icon className="w-4 h-4" component={() => <SpacesIcon />} />
                <Typography.Text className="text-sm font-medium text-gray-500">Spaces</Typography.Text>
              </div>
              {spaces
                .filter((s) => s.id === activeSpaceId)
                .map((space) => {
                  const isAdmin = space?.currentSpaceMember?.role === Pto.Spaces.SpaceMemberRole.Admin
                  const settingsLinks = isAdmin
                    ? generateSpaceSettingsMenu(
                        space.workspaceId,
                        space.id,
                        space.currentSpaceMember?.role,
                        onMenuItemClick
                      )
                    : [
                        {
                          key: `/settings/${space.workspaceId}/space/${space.id}/members`,
                          label: (
                            <Link
                              to={`/settings/${space.workspaceId}/space/${space.id}/members`}
                              onClick={onMenuItemClick}
                            >
                              Members
                            </Link>
                          )
                        }
                      ]

                  return (
                    <div key={space.id}>
                      <div className="px-4 py-2 flex items-center">
                        {space.status === Pto.Spaces.SpaceStatus.Inactive && (
                          <StopOutlined className="text-red-500 mr-1" />
                        )}
                        <Typography.Text className="text-sm font-medium text-gray-500">{space.name}</Typography.Text>
                      </div>
                      {settingsLinks.map((link) => (
                        <Link key={link.key} to={link.key as string} onClick={onMenuItemClick} className="block">
                          <Button
                            type="text"
                            className="w-full flex h-10 justify-normal hover:!bg-secondaryWithOpacity font-medium pl-12"
                          >
                            {link.label}
                          </Button>
                        </Link>
                      ))}
                    </div>
                  )
                })}
            </>
          )}
          <Divider className="mx-0 my-2" />
          <Button
            type="text"
            icon={<LogoutOutlined className="[&>svg]:w-[18px] [&>svg]:h-[18px]" />}
            className="w-full flex h-10 justify-normal mr-auto hover:!bg-secondaryWithOpacity font-medium"
            onClick={() => authStorage.signOut()}
          >
            Log out
          </Button>
        </Flex>
      </div>
    )
  }

  return (
    <div className="max-xl:pb-4 md:sticky xl:block bottom-0 bg-white">
      <Flex gap={12} className={clsx('px-1', [collapsed && 'flex-col'])}>
        {workspace && !!workspace.currentMember && (
          <>
            <ConditionalWrapper
              Wrapper={Link}
              wrapperProps={{
                to: `${AppRoutes.settings}/${params.workspaceId}/profile-settings`,
                onClick: onMenuItemClick
              }}
              condition={!user?.roles.includes(Pto.Users.UserRole.SystemAdmin)}
            >
              <Button
                type="text"
                icon={<UserOutlined className="[&>svg]:w-[18px] [&>svg]:h-[18px] -ml-px" />}
                className="w-full flex h-10 justify-normal hover:!bg-secondaryWithOpacity font-medium"
                onClick={onSettingsClick}
              >
                Profile
              </Button>
            </ConditionalWrapper>
            {workspace.currentMember.role !== Pto.Workspaces.WorkspaceRole.Contact && (
              <Button
                type="text"
                icon={<SettingOutlined className="[&>svg]:w-[18px] [&>svg]:h-[18px] -ml-px" />}
                className="w-full flex h-10 justify-normal hover:!bg-secondaryWithOpacity font-medium"
                onClick={onSettingsClick}
              >
                Settings
              </Button>
            )}
            {workspace.currentMember.role === Pto.Workspaces.WorkspaceRole.Contact && (
              <Feedback isNavMenuLink onOpen={onMenuItemClick} />
            )}
          </>
        )}
        {user?.roles.includes(Pto.Users.UserRole.SystemAdmin) && (
          <Flex gap={10}>
            <UserOutlined className="[&>svg]:w-[24px] [&>svg]:h-[24px] -ml-px" />
            <Flex vertical justify="center" className={clsx('max-w-56 lg:max-w-36', [collapsed && 'hidden'])}>
              <Typography.Paragraph className="font-medium !mb-0 text-sm whitespace-nowrap text-ellipsis overflow-hidden">
                {user?.firstName} {user?.lastName}
              </Typography.Paragraph>
              <Typography.Text className="text-sm whitespace-nowrap text-ellipsis overflow-hidden">
                {user?.email}
              </Typography.Text>
            </Flex>
          </Flex>
        )}
        <Button
          type="text"
          icon={<LogoutOutlined className="[&>svg]:w-[18px] [&>svg]:h-[18px]" />}
          className="w-full flex h-10 justify-normal mr-auto hover:!bg-secondaryWithOpacity font-medium"
          onClick={() => authStorage.signOut()}
        >
          Log out
        </Button>
      </Flex>
    </div>
  )
}

export default UserMenuItem
